import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  Link,
  LinkProps,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { CloseIcon, HamburgerIcon, SettingsIcon } from '@chakra-ui/icons';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { AiOutlineLogout } from 'react-icons/ai';
import SignInModal from '~/components/organisms/SignInModal';
import { UserAvatar } from '~/components/molecules';
import { useUser } from '~/containers';
import { AuthState } from '@aws-amplify/ui-components';
import { JobSeeker } from '~/generated';
import { myProfile } from '~/queries';
import { useQuery } from '@apollo/client';
import logo from './logo-with-text.png';

const CONTENTS_LINK =
  'https://nova-okapi-8fd.notion.site/302cb21c0813452dbb788fcfe197ffca';

const Links = [
  // { to: '/jobs', text: '求人一覧' },
  { to: '/topics', text: '募集特集' },
  { to: '/messages', text: 'メッセージ' },
  { to: '/likes', text: '興味あり' },
  // { to: '/contents', text: '就活に役立つコンテンツ集' },
  { to: '/campaign', text: '友人紹介キャンペーン' },
];

const InternalOnlyLinks: typeof Links = [];

type NavLinkProps = LinkProps & {
  children: React.ReactNode;
  to: string;
};

const NavLink = (props: NavLinkProps) => {
  const { to, children, isExternal } = props;
  const routerProps = isExternal ? { href: to } : { as: RouterLink };

  return (
    <Link
      px={2}
      py={1}
      rounded="md"
      isExternal={isExternal}
      to={to}
      _hover={{
        textDecoration: 'none',
        bg: useColorModeValue('gray.200', 'gray.700'),
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...routerProps}
    >
      {children}
    </Link>
  );
};

const UserLinks = () => {
  const navigate = useNavigate();
  const { isOpen, onToggle } = useDisclosure();
  const { user, logout, image } = useUser();
  const [authState, setAuthState] = useState<
    AuthState.SignIn | AuthState.SignUp
  >(AuthState.SignIn);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return user ? (
    <Menu>
      <MenuButton as={Button} rounded="full" variant="link" cursor="pointer">
        <UserAvatar
          name={user?.attributes?.family_name}
          imageKey={image}
          size="md"
        />
      </MenuButton>
      <MenuList>
        <Link
          to="/profile"
          as={RouterLink}
          _hover={{
            textDecoration: 'none',
          }}
        >
          <MenuItem>
            <SettingsIcon /> &nbsp;プロフィール
          </MenuItem>
        </Link>

        <MenuItem onClick={handleLogout}>
          <AiOutlineLogout />
          &nbsp; ログアウト
        </MenuItem>
      </MenuList>
    </Menu>
  ) : (
    <>
      <Button
        display={{ md: 'inline-flex' }}
        fontSize="sm"
        fontWeight={600}
        color="white"
        bg="pink.400"
        onClick={() => {
          setAuthState(AuthState.SignUp);
          onToggle();
        }}
        _hover={{
          bg: 'pink.300',
        }}
      >
        登録
      </Button>
      <Button
        display={{ md: 'inline-flex' }}
        fontSize="sm"
        fontWeight={600}
        color="white"
        bg="pink.400"
        onClick={() => {
          setAuthState(AuthState.SignIn);
          onToggle();
        }}
        _hover={{
          bg: 'pink.300',
        }}
      >
        ログイン
      </Button>
      <SignInModal
        isOpen={isOpen}
        onClose={onToggle}
        defaultAuthState={authState}
      />
    </>
  );
};

const CambiaHeader: React.FC = () => {
  const { isOpen, onToggle } = useDisclosure();
  const { user } = useUser();
  const { data } = useQuery<{ jobSeekerMe: JobSeeker }>(myProfile, {
    skip: !user,
  });

  const links = useMemo<typeof Links>(() => {
    if (!user) {
      return [];
    }
    if (data?.jobSeekerMe?.isInternal) {
      return [...Links, ...InternalOnlyLinks];
    }

    return Links;
  }, [user, data]);

  return (
    <Box
      bg={useColorModeValue('gray.100', 'gray.900')}
      px={4}
      position="sticky"
      zIndex={100}
      top={0}
    >
      <Flex h={16} alignItems="center" justifyContent="space-between">
        <IconButton
          size="md"
          icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
          aria-label="Open Menu"
          display={{ md: !isOpen ? 'none' : 'inherit' }}
          onClick={onToggle}
        />
        <HStack spacing={8} alignItems="center">
          <Box>
            <Image src={logo} h="60px" />
          </Box>
          <HStack as="nav" spacing={4} display={{ base: 'none', md: 'flex' }}>
            {links.map((link) => (
              <NavLink key={link.to} to={link.to}>
                <Text fontWeight={600}>{link.text}</Text>
              </NavLink>
            ))}
            {user && (
              <NavLink to={CONTENTS_LINK} isExternal>
                <Text fontWeight={600}>就活に役立つコンテンツ集</Text>
              </NavLink>
            )}
          </HStack>
        </HStack>
        <Flex alignItems="center">
          <HStack spacing={8} alignItems="center">
            <UserLinks />
          </HStack>
        </Flex>
      </Flex>

      {isOpen ? (
        <Box pb={4}>
          <Stack as="nav" spacing={4}>
            {Links.map((link) => (
              <NavLink key={link.to} to={link.to}>
                {link.text}
              </NavLink>
            ))}
            <NavLink to={CONTENTS_LINK} isExternal>
              <Text>就活に役立つコンテンツ集</Text>
            </NavLink>
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
};

export default CambiaHeader;
